import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { downloadCsv, useEnterpriseList, useFilterOptions } from "../api/hooks";
import type { EnterpriseFilterState } from "../api/types";
import { EnterpriseDetailPanel } from "./EnterpriseDetailPanel";
import { EnterpriseFilters } from "./EnterpriseFilters";
import { EnterpriseTable } from "./EnterpriseTable";
import { PaginationBar, QueryState } from "./QueryState";

const PAGE_SIZE = 50;

export function EnterpriseListView({
  onViewMember,
}: {
  onViewMember?: (
    profileId: string,
    name: string,
    tab: "diagnostic" | "plan",
  ) => void;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<EnterpriseFilterState>({});
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: options } = useFilterOptions();
  const { data, isLoading, isError, isFetching } = useEnterpriseList(filters, page);

  const applyFilters = (next: EnterpriseFilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await downloadCsv("/api/v1/reports/enterprises/csv/", filters, "entreprises.csv");
    } catch {
      toast.error(t("reports.csv.failed"));
    }
  };

  return (
    <div className="space-y-4">
      <EnterpriseFilters
        value={filters}
        onChange={applyFilters}
        options={options}
        showAnswerFilters
        onExport={handleExport}
        exportLabel={t("reports.csv.export")}
      />

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!isFetching && data?.count === 0}
      >
        {data ? (
          <>
            <EnterpriseTable rows={data.results} onSelect={setSelectedId} />
            <PaginationBar
              page={page}
              count={data.count}
              pageSize={PAGE_SIZE}
              onPage={setPage}
            />
          </>
        ) : null}
      </QueryState>

      {selectedId ? (
        <EnterpriseDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onViewMember={onViewMember}
        />
      ) : null}
    </div>
  );
}
